import { useState } from 'react';

type Status = 'fehlt' | 'vorhanden';

export default function KontoauszugVariantenPreview() {
  const [status, setStatus] = useState<Status>('fehlt');

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Kontoauszug-Bitte — Varianten</h1>
        <p className="text-sm text-slate-600 mb-6">
          Drei mögliche Ansätze, wie der Berater die Erinnerung an den Kontoauszug steuert.
          Die Vorschauen sind klickbar (Checkboxen reagieren) — keine Daten werden gesendet.
        </p>

        <div className="mb-6 flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200 w-fit">
          <span className="text-xs font-medium text-slate-500">Demo-Status im Vorgang:</span>
          <button
            onClick={() => setStatus('fehlt')}
            className={`px-3 py-1 text-xs rounded-md ${
              status === 'fehlt' ? 'bg-amber-100 text-amber-800 font-medium' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            Kontoauszug fehlt
          </button>
          <button
            onClick={() => setStatus('vorhanden')}
            className={`px-3 py-1 text-xs rounded-md ${
              status === 'vorhanden' ? 'bg-emerald-100 text-emerald-800 font-medium' : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            Kontoauszug vorhanden
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <VariantA status={status} />
          <VariantB status={status} />
          <VariantC status={status} />
        </div>

        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-700">
          <p className="font-semibold mb-2">Zusammenfassung</p>
          <ul className="space-y-1.5 list-disc list-inside">
            <li><strong>A:</strong> Checkbox immer da, default AUS — du entscheidest aktiv jedes Mal.</li>
            <li><strong>B:</strong> Checkbox immer da, Status-Hinweis + Smart-Default (an wenn fehlt, aus wenn vorhanden).</li>
            <li><strong>C:</strong> Eigene Karte 'Kontoauszug anfordern' — komplett getrennt vom Signing-Flow.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function VariantA({ status: _status }: { status: Status }) {
  const [checked, setChecked] = useState(false);
  return (
    <DialogFrame title="A: Checkbox immer, default AUS">
      <Recipient />
      <Documents />
      <div className="border-t border-slate-200 pt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">Kontoauszug</p>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => setChecked(v => !v)}
            className="mt-0.5 rounded border-slate-300"
          />
          <span>Erinnerung an Kontoauszug in Mail mitschicken</span>
        </label>
        <p className="text-xs text-slate-400 mt-1 ml-6">Default aus — du entscheidest pro Mail.</p>
      </div>
      <DialogFooter />
    </DialogFrame>
  );
}

function VariantB({ status }: { status: Status }) {
  const [checked, setChecked] = useState(status === 'fehlt');
  // Reset bei Status-Wechsel
  const key = `${status}`;
  return (
    <DialogFrame title="B: Smart-Default">
      <Recipient />
      <Documents />
      <div className="border-t border-slate-200 pt-3" key={key}>
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">Kontoauszug</p>
        {status === 'vorhanden' ? (
          <>
            <p className="text-sm text-emerald-700 flex items-center gap-1.5 mb-2">
              <span>✓</span> Bereits im Vorgang vorhanden
            </p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                defaultChecked={false}
                onChange={e => setChecked(e.target.checked)}
                className="mt-0.5 rounded border-slate-300"
              />
              <span>Erinnerung trotzdem mitschicken</span>
            </label>
          </>
        ) : (
          <>
            <p className="text-sm text-amber-700 flex items-center gap-1.5 mb-2">
              <span>⚠</span> Kein Kontoauszug im Vorgang
            </p>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                defaultChecked={true}
                onChange={e => setChecked(e.target.checked)}
                className="mt-0.5 rounded border-slate-300"
              />
              <span>
                <span className="font-medium">Erinnerung an Kontoauszug in Mail mitschicken</span>
                <span className="block text-xs text-slate-500">(vorgeschlagen weil keiner vorhanden)</span>
              </span>
            </label>
          </>
        )}
        <p className="text-xs text-slate-400 mt-2">Aktuell: {checked ? 'AN' : 'AUS'}</p>
      </div>
      <DialogFooter />
    </DialogFrame>
  );
}

function VariantC({ status }: { status: Status }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-900 mb-1">C: Eigene Karte</h3>
      <p className="text-xs text-slate-500 mb-4">Im Editor neben 'Unterschreiben' eine eigenständige Karte.</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <CardMock num="2" title="Unterschreiben" subtitle="Unterlagen zur Unterschrift senden" btn="Senden" color="emerald" />
        <CardMock
          num="3"
          title="Kontoauszug"
          subtitle={
            status === 'vorhanden'
              ? '✓ Bereits vorhanden — nichts zu tun'
              : 'Kunde per Mail um Kontoauszug bitten'
          }
          btn={status === 'vorhanden' ? '—' : 'Anfordern'}
          color={status === 'vorhanden' ? 'slate' : 'blue'}
          disabled={status === 'vorhanden'}
        />
      </div>

      <div className="border-t border-slate-200 pt-3">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">Beim Klick auf 'Anfordern':</p>
        <div className="text-xs bg-slate-50 border border-slate-200 rounded p-3 text-slate-700">
          Eigene kurze Mail an Kunden:<br />
          <em>"Bitte laden Sie hier Ihren aktuellen Kontoauszug hoch: [Link]"</em><br />
          <span className="text-slate-500">Eigene Upload-Seite, KI-Check, Event im Verlauf.</span>
        </div>
      </div>
    </div>
  );
}

function CardMock({
  num, title, subtitle, btn, color, disabled,
}: {
  num: string; title: string; subtitle: string; btn: string;
  color: 'emerald' | 'blue' | 'slate'; disabled?: boolean;
}) {
  const ringClass =
    color === 'emerald' ? 'bg-emerald-600' :
    color === 'blue' ? 'bg-blue-600' :
    'bg-slate-300';
  const btnClass =
    color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' :
    color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
    'bg-slate-200 text-slate-500';
  return (
    <div className="rounded-lg border border-slate-200 p-3 flex flex-col">
      <div className="flex items-start gap-2 mb-2">
        <div className={`w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center ${ringClass}`}>{num}</div>
        <div>
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
      </div>
      <button
        disabled={disabled}
        className={`mt-auto text-xs text-white px-2.5 py-1.5 rounded-md ${btnClass} disabled:opacity-60`}
      >
        {btn}
      </button>
    </div>
  );
}

function DialogFrame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="border-b border-slate-200 px-5 py-3">
        <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Recipient() {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Empfänger</p>
      <p className="text-sm text-slate-800">kunde@firma.de</p>
    </div>
  );
}

function Documents() {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1">Dokumente</p>
      <ul className="text-sm space-y-1">
        <li className="flex items-center gap-2"><input type="checkbox" defaultChecked className="rounded border-slate-300" /> <span>Bericht</span></li>
        <li className="flex items-center gap-2 text-slate-400"><input type="checkbox" disabled /> <span className="line-through">Vollmacht (übersprungen)</span></li>
      </ul>
    </div>
  );
}

function DialogFooter() {
  return (
    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
      <button className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Abbrechen</button>
      <button className="px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md">Anfrage senden</button>
    </div>
  );
}
